import Layout from "../Layout/Layout.component";
import { useContext, useEffect, useState } from "react";
import { PAGE_STATUS } from "../interfaces";
import Loading from "../Components/Loading.component";
import ClientAxios from "../Client.axios";
import { AppContext } from "../Context/MainContext";
import InfoCard from "../Components/InfoCard.component";
import { formatCurrency } from "../utils/number.utils";
import { calculateControlSheetData } from "../utils/controlSheet.utils";
import { getSpendsByFamilies, getSpendsByWorkload, getNnccByFamilies } from "../utils/planification.utils";

const DashboardPage = () => {

    const [status, setStatus] = useState(PAGE_STATUS.LOADING);
    const [data, setData] = useState<any>({});
    const { projects, planifications, selectedProject, transactions, controlSheets } = useContext(AppContext);
    const [showAllApproved, setShowAllApproved] = useState(false);

    useEffect(() => {
        if (projects.length === 0) {
            setStatus(PAGE_STATUS.NO_PROJECTS);
        } else if (!selectedProject) {
            setStatus(PAGE_STATUS.NO_SELECTED_PROJECT);
        } else {
            setStatus(PAGE_STATUS.SUCCESS);
            let familySpends = getSpendsByFamilies(transactions, planifications, selectedProject, false, showAllApproved);
            let familyNnccs = getNnccByFamilies(transactions, selectedProject);
            let workloadSpends: { [key: string]: number } = getSpendsByWorkload(planifications, selectedProject);

            let workLoadResumes = selectedProject.workload.map((workload: any) => {
                return {
                    ...workload,
                    spends: workloadSpends[workload.name] || 0,
                    remaining: workload.budget - (workloadSpends[workload.name] || 0),
                    percentage: ((workloadSpends[workload.name] || 0) / workload.budget) * 100
                }
            });

            let workLoadResume = {
                budget: selectedProject.workload.reduce((acc: number, workload: any) => acc + workload.budget, 0),
                spends: workLoadResumes.reduce((acc: number, workload: any) => acc + workload.spends, 0),
                remaining: selectedProject.workload.reduce((acc: number, workload: any) => acc + workload.budget, 0) - workLoadResumes.reduce((acc: number, workload: any) => acc + workload.spends, 0),
                percentage: (workLoadResumes.reduce((acc: number, workload: any) => acc + workload.spends, 0) / selectedProject.workload.reduce((acc: number, workload: any) => acc + workload.budget, 0)) * 100
            }

            let familyResumes = Object.keys(familySpends).map((family: string) => {
                let nncc = familyNnccs[family] || 0;
                let originalSpend = familySpends[family];
                familySpends[family] = originalSpend - nncc;

                if (family === "Sin familia")
                    return {
                        family,
                        spends: familySpends[family],
                        nncc,
                        hasWorkload: false,
                        spendsWorkload: 0,
                        remaining: 0,
                        percentage: 0
                    }

                let budget = selectedProject.families.find((f: any) => f.name === family);
                budget = budget.budget;
                let workload = workLoadResumes.filter((workload: any) => workload.family === family);
                let hasWorkload = workload.length > 0;
                let spendsWorkload = workload.reduce((acc: number, workload: any) => acc + workload.spends, 0);
                let spend = familySpends[family];
                let totalSpend = spend + spendsWorkload - nncc;
                let remaining = budget - totalSpend;
                let percentage = (totalSpend / budget) * 100;

                return {
                    family,
                    budget: budget,
                    spends: familySpends[family],
                    nncc,
                    hasWorkload,
                    spendsWorkload,
                    totalSpend,
                    remaining,
                    percentage
                }
            });

            let tmp: number[] = Object.values(familySpends)
            let totalNncc = Object.values(familyNnccs).reduce((acc: number, nncc: any) => acc + (nncc || 0), 0) as number;

            let totalAcuerdosPorPagar = 0;
            controlSheets.forEach((cs: any) => {
                let d: any = calculateControlSheetData(cs, transactions);

                let invoices = d.invoices.filter((t: any) => {
                    const statusId = t.status?._id || t.status;
                    return statusId === 'Ingresada' || statusId === 'Aprobada';
                });

                let nnccs = d.NNCCs
                    .filter((t: any) => t.controlSheet && (t.controlSheet._id === cs._id || t.controlSheet === cs._id))
                    .reduce((acc: number, curr: any) => acc + (curr.total || 0), 0);

                let filteredInvoicesSum = invoices.filter((t: any) => showAllApproved ? true : (t.paymentStatus && t.paymentStatus.toLowerCase().includes("pagada"))).reduce((acc: number, curr: any) => acc + (curr.total || 0), 0);

                let finalFacturasVal = filteredInvoicesSum - nnccs;

                let diff = (d.agreementsTotal || 0) - finalFacturasVal;
                totalAcuerdosPorPagar += diff;
            });

            setData({
                spends: familySpends,
                familyResumes,
                workloadSpends,
                workLoadResumes,
                workLoadResume,
                totalSpends: tmp.reduce((acc: number, spend: number) => acc + spend, 0) + Object.values(workloadSpends).reduce((acc: number, spend: number) => acc + spend, 0),
                totalNncc: totalNncc,
                totalAcuerdosPorPagar,
                budget: selectedProject.budgets.find((budget: any) => budget.isMain).amount
            });
        }
    }, [projects, selectedProject, transactions, showAllApproved]);

    return (<Layout>
        <div className="container">
            <Loading status={status} />
            {status === PAGE_STATUS.NO_PROJECTS &&
                <div>
                    <h1 className="text-2xl font-bold mb-4">Error</h1>
                    <p>Usted no tiene proyectos asigandos, por favor contactese con Syncroniza si considera que esto no
                        es correcto</p>
                </div>
            }
            {
                status === PAGE_STATUS.NO_SELECTED_PROJECT &&
                <div>
                    <h1 className="text-2xl font-bold mb-4">Error</h1>
                    <p>Usted no tiene un proyecto seleccionado, por favor seleccione uno</p>
                </div>
            }
            {
                status === PAGE_STATUS.SUCCESS &&
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-bold">Dashboard: {selectedProject.name}</h1>
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                            <input
                                type="checkbox"
                                checked={showAllApproved}
                                onChange={(e) => setShowAllApproved(e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span>Mostrar todas las facturas aprobadas e ingresadas (por defecto: sólo pagadas)</span>
                        </label>
                    </div>
                    <div className="info-cards">
                        <div className="info-card flex gap-2">
                            <InfoCard
                                title="Duración del proyecto"
                                content={[
                                    `Fecha de inicio: ${new Date(selectedProject.startDate).toLocaleDateString()}`,
                                    `Fecha de fin: ${new Date(selectedProject.endDate).toLocaleDateString()}`,
                                    `Duración: ${planifications.length} semanas`,
                                    `Semana actual: ${planifications.findIndex((planification: any) => {
                                        return new Date() >= new Date(planification.startDate) && new Date() <= new Date(planification.endDate);
                                    }) + 1}/${planifications.length}`
                                ]}
                            />
                            <InfoCard
                                title="Progreso"
                                content={[
                                    `Presupuesto: $ ${formatCurrency(data.budget)}`,
                                    `Gastado: $ ${formatCurrency(data.totalSpends)}`,
                                    `NNCC: $ ${formatCurrency(data.totalNncc)}`,
                                    `Restante: $ ${formatCurrency(data.budget - data.totalSpends + data.totalNncc)}`,
                                    `Porcentaje gastado: ${((data.totalSpends / data.budget) * 100).toFixed(1)}%`
                                ]}
                            />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold my-4">Presupuesto</h2>
                            <div className="info-card flex gap-2">
                                {selectedProject.budgets.sort((a: any, b: any) => a.isMain).map((budget: any, index: number) => (
                                    <InfoCard
                                        key={index}
                                        title={budget.name}
                                        content={[
                                            `Monto: $ ${formatCurrency(budget.amount)}`,
                                        ]}
                                    />
                                ))}

                            </div>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold my-4">Familias</h2>
                            <div className="info-card flex gap-2">
                                {data.familyResumes.map((resume: any, index: number) => {
                                    let content = [];
                                    if (resume.family.toLowerCase().includes("utilidades")) {
                                        return null;
                                    }


                                    if (resume.hasWorkload) {
                                        content.push(`Presupuesto Total: $ ${formatCurrency(resume.budget)}`);
                                        content.push(`Gastado S/MO: $ ${formatCurrency(resume.spends)}`);
                                        content.push(`Gastado C/MO: $ ${formatCurrency(resume.spendsWorkload + resume.spends)}`);
                                        if (resume.nncc > 0) {
                                            content.push(`NNCC: $ ${formatCurrency(resume.nncc)}`);
                                        }
                                        content.push(`Gastado Total: $ ${formatCurrency(resume.totalSpend)}`);
                                        content.push(`Restante: $ ${formatCurrency(resume.remaining)}`);
                                        content.push(`Porcentaje gastado: ${resume.percentage.toFixed(1)}%`);

                                    } else {
                                        content.push(`Presupuesto: $ ${formatCurrency(resume.budget)}`);
                                        content.push(`Gastado: $ ${formatCurrency(resume.spends)}`);
                                        if (resume.nncc > 0) {
                                            content.push(`NNCC: $ ${formatCurrency(resume.nncc)}`);
                                        }
                                        content.push(`Gastado Total: $ ${formatCurrency(resume.totalSpend)}`);
                                        content.push(`Restante: $ ${formatCurrency(resume.remaining)}`);
                                        content.push(`Porcentaje gastado: ${resume.percentage.toFixed(1)}%`);
                                    }

                                    return (<InfoCard
                                        key={index}
                                        title={resume.family}
                                        content={content}
                                    />
                                    )
                                })}
                                {/* <InfoCard
                                    title="Mano de Obra"
                                    content={[
                                        `Gastado: $ ${formatCurrency(data.workLoadResume.spends)}`,
                                        `Presupuesto: $ ${formatCurrency(data.workLoadResume.budget)}`,
                                        `Restante: $ ${formatCurrency(data.workLoadResume.remaining)}`,
                                        `Porcentaje gastado: ${data.workLoadResume.percentage.toFixed(1)}%`
                                    ]}
                                /> */}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold my-4">Mano de Obra</h2>
                            <div className="info-card flex gap-2">
                                {data.workLoadResumes.map((workload: any, index: number) => (
                                    <InfoCard
                                        key={index}
                                        title={workload.name}
                                        content={[
                                            `Familia: ${workload.family}`,
                                            `Presupuesto: $ ${formatCurrency(workload.budget)}`,
                                            `Gastado: $ ${formatCurrency(workload.spends)}`,
                                            `Restante: $ ${formatCurrency(workload.remaining)}`,
                                            `Porcentaje gastado: ${workload.percentage.toFixed(1)}%`
                                        ]}
                                    />
                                ))}
                            </div>

                        </div>

                        {
                            selectedProject.families.find((f: any) => f.name.toLowerCase().includes("utilidades")) &&
                            <div>
                                <h2 className="text-2xl font-bold my-4">Utilidades</h2>
                                <div className="info-card flex gap-2">
                                    {selectedProject.families.filter((f: any) => f.name.toLowerCase().includes("utilidades")).map((family: any, index: number) => (
                                        <InfoCard
                                            extraClass={"bg-gradient-to-b from-green-400 to-green-600"}
                                            key={index}
                                            title={family.name}
                                            content={[
                                                `Presupuesto: $ ${formatCurrency(family.budget)}`,
                                            ]}
                                        />
                                    ))}

                                    {
                                        (data.totalSpends - data.totalNncc) < data.budget &&
                                        <div className="info-card flex gap-2">
                                            <InfoCard
                                                extraClass={"bg-gradient-to-b from-green-400 to-green-600"}
                                                title="Sobreutilidad esperada"
                                                content={[
                                                    `Valor: $ ${formatCurrency(data.budget - (data.totalSpends - data.totalNncc) - data.totalAcuerdosPorPagar)}`,
                                                    `Acuerdos por pagar: $ ${formatCurrency(data.totalAcuerdosPorPagar)}`,
                                                ]}
                                            />
                                        </div>
                                    }
                                    {
                                        (data.totalSpends - data.totalNncc) > data.budget &&
                                        <div className="info-card flex gap-2">
                                            <InfoCard
                                                extraClass={"bg-gradient-to-b from-red-400 to-red-600"}
                                                title="Perdida esperada"
                                                content={[
                                                    `Valor: - $ ${formatCurrency(data.budget - (data.totalSpends - data.totalNncc) - data.totalAcuerdosPorPagar)}`,
                                                    `Acuerdos por pagar: $ ${formatCurrency(data.totalAcuerdosPorPagar)}`,
                                                ]}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                    </div>
                </div>
            }
        </div>
    </Layout>
    );
}

export default DashboardPage;